<template>
  <div style="width: 100%" class="fill-height">
    <v-container>
      <v-row class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']">
          <!-- Sticky Header with Filters -->
          <div v-if="listView" class="table-header-filters">
            <v-row dense class="align-center">
              <v-col cols="12" sm="6" md="3" lg="2">
                <v-select
                  dense
                  outlined
                  label="Prodajno mjesto"
                  v-model="selectedLocation"
                  :items="locations"
                  item-text="name"
                  item-value="id"
                  return-object
                  @change="setWarehouseChoices"
                  hide-details
                  class="filter-select"
                >
                </v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="2">
                <v-select
                  dense
                  outlined
                  label="Skladište"
                  v-model="selectedWarehouse"
                  :items="warehouses"
                  item-text="name"
                  item-value="value"
                  return-object
                  @change="applyFilters"
                  hide-details
                  class="filter-select"
                  :disabled="!selectedLocation || !selectedLocation.id"
                >
                  <template slot="item" slot-scope="data">
                    {{ data.item.warehouse.location.location_id }} -
                    {{ data.item.warehouse.name }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="8" md="4" lg="3">
                <v-text-field
                  v-model="searchTerm"
                  label="Pretraži artikle"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="filter-select"
                  @input="onSearchInput"
                  @click:clear="clearSearch"
                  :disabled="!selectedWarehouse || !selectedWarehouse.value"
                >
                  <template #append>
                    <v-icon color="#3989C9" small>mdi-magnify</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="2" lg="1">
                <v-btn
                  class="search-btn"
                  @click="applySearch"
                  :loading="isLoading"
                  :disabled="!selectedWarehouse || !selectedWarehouse.value || isLoading"
                  elevation="0"
                  small
                  block
                >
                  <v-icon small class="mr-1">mdi-magnify</v-icon>
                  Pretraži
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4" md="2" lg="1" class="ml-auto">
                <v-btn
                  class="search-btn"
                  @click="exportItemsCsv"
                  :disabled="!items.length"
                  elevation="0"
                  small
                  block
                >
                  <v-icon small class="mr-1">mdi-file-delimited-outline</v-icon>
                  CSV
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Items List -->
          <div v-if="listView">
            <div v-if="isLoading" class="loading-overlay-receipts">
              <v-progress-circular
                indeterminate
                size="64"
                class="loading-spinner"
              ></v-progress-circular>
            </div>
          <div class="warehouse-rows-body">
          <v-card
            v-for="(extendedItem, i) in items"
            :key="i"
            :class="['warehouse-item-card', extendedItem.item.status === 'OK' ? 'status-ok' : extendedItem.item.status === 'PROCESSING' ? 'status-warn' : 'status-failed']"
          >
            <v-row class="ma-0" style="min-height: 60px !important">
              <div
                v-if="density !== 'greater'"
                style="width: 6% !important; display: flex; align-items: center; justify-content: center; padding: 8px;"
                :class="[densityPadding]"
              >
                <v-img
                  v-if="extendedItem.item.picture"
                  :src="extendedItem.item.picture"
                  contain
                  width="44"
                  height="44"
                  style="border-radius: 6px;"
                ></v-img>
                <div v-else style="width:44px; height:44px; border-radius:6px; background:#F1F5F9;"></div>
              </div>
              <v-col
                style="min-width: 400px; max-width: 90%"
                :class="[densityPadding]"
              >
                <div class="wh-item-name one-liner-text"
                  @mouseover="hoverOver(`hover-${extendedItem.item.id}`, extendedItem.item)"
                  @mouseleave="extendedItem.item[`hover-${extendedItem.item.id}`] = false"
                  :id="`hover-${extendedItem.item.id}`"
                  :ref="`hover-${extendedItem.item.id}`"
                >
                  {{ $options.filters.capitalize(extendedItem.item.name) }}
                </div>
              </v-col>
              <v-col
                style="min-width: 50px; max-width: 10%"
                :class="[densityPadding]"
              >
                <div class="caption grey--text">Šifra</div>
                <div class="wh-cell-value">{{ extendedItem.item.code }}</div>
              </v-col>
              <v-col
                :class="[densityPadding]"
                style="min-width: 100px; max-width: 10%"
              >
                <div class="caption grey--text text-center">Stanje</div>
                <div class="wh-cell-value text-center">
                  {{
                    extendedItem.item.type === "GOODS" && extendedItem.item.stock !== undefined
                      ? extendedItem.item.stock.toString().replace(".", ",")
                      : "-"
                  }}
                </div>
              </v-col>
              <v-col
                :class="[densityPadding]"
                style="min-width: 100px; max-width: 20%"
              >
                <div v-if="extendedItem.unit !== undefined && extendedItem.unit !== ''" class="caption grey--text text-center">Mj. jedinica</div>
                <div v-if="extendedItem.unit !== undefined && extendedItem.unit !== ''" class="wh-cell-value text-center">{{ extendedItem.unit }}</div>
              </v-col>
              <v-col :class="[densityPadding]">
                <div class="caption grey--text text-center">Status</div>
                <div class="wh-cell-value text-center">{{ extendedItem.item.status }}</div>
              </v-col>
              <v-col cols="auto" class="d-flex align-center pa-2">
                <v-btn
                  icon
                  small
                  color="error"
                  @click.stop="openDeleteDialog(extendedItem)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
          </div>
          </div>

          <!-- Load More Button -->
          <v-row v-if="hasMoreItems" class="justify-center mt-4 mb-4">
            <v-btn
              class="page-nav-btn-header"
              @click="loadMore"
              :loading="isLoading"
              :disabled="isLoading"
              elevation="2"
            >
              Učitaj više
              <v-icon small class="ml-2">mdi-chevron-down</v-icon>
            </v-btn>
          </v-row>

        </v-col>
      </v-row>
    </v-container>
    <add-warehouse-module-item
      ref="addWarehouseModuleItem"
      @success="refreshWarehouse"
    ></add-warehouse-module-item>

    <v-dialog v-model="deleteDialog" max-width="520" @click:outside="deleteDialog = false">
      <v-card class="del-modal-card" style="height: 480px; display: flex; flex-direction: column;">

        <!-- Header -->
        <div class="del-modal-header">
          <div class="del-modal-header-gloss"></div>
          <div class="del-modal-header-content">
            <v-icon color="white" size="22" class="mr-2">mdi-delete-outline</v-icon>
            <div>
              <div class="del-modal-title">Brisanje artikla sa skladišta</div>
              <div v-if="itemToDelete" class="del-modal-subtitle">
                {{ itemToDelete.item && itemToDelete.item.name }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small @click="deleteDialog = false" class="del-modal-close-btn">
              <v-icon color="white" size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Body -->
        <v-card-text class="pt-5 pb-3 px-6" style="flex: 1; overflow-y: auto; min-height: 0;">

          <!-- Loading -->
          <div v-if="deleteLoading" class="d-flex align-center justify-center py-8">
            <v-progress-circular indeterminate color="#3989C9" size="38" width="3"></v-progress-circular>
            <span class="ml-4 body-2" style="color:#64748b;">Provjera u tijeku...</span>
          </div>

          <!-- Stanje nije 0 -->
          <template v-else-if="deleteErrorMsg === 'nonzero_stock'">
            <div class="del-info-box del-info-box--warn mb-4">
              <v-icon size="20" color="#b45309" class="mr-3 flex-shrink-0">mdi-alert-circle-outline</v-icon>
              <span>Stanje artikla mora biti <strong>0</strong> prije brisanja.</span>
            </div>
            <div class="del-detail-row">
              <v-icon size="18" color="#94a3b8" class="mr-2">mdi-package-variant-closed</v-icon>
              <span class="caption" style="color:#64748b;">Trenutno stanje:</span>
              <span class="ml-2 font-weight-semibold" style="color:#b45309;">
                {{ itemToDelete && itemToDelete.item && itemToDelete.item.stock }}
                {{ itemToDelete && itemToDelete.unit }}
              </span>
            </div>
          </template>

          <!-- Na cjeniku -->
          <template v-else-if="deleteErrorMsg === 'on_pricelist'">
            <div class="del-info-box del-info-box--warn mb-4">
              <v-icon size="20" color="#b45309" class="mr-3 flex-shrink-0">mdi-tag-outline</v-icon>
              <span>Artikl je aktivan na cjeniku. Uklonite ga s cjenika prije brisanja.</span>
            </div>
            <div class="del-table-label">AKTIVAN NA PRODAJNIM MJESTIMA</div>
            <div class="del-table">
              <div v-for="name in pricelistLocations" :key="name" class="del-table-row">
                <v-icon size="16" color="#94a3b8" class="mr-2">mdi-store-outline</v-icon>
                <span>{{ name }}</span>
              </div>
            </div>
          </template>

          <!-- U normativu -->
          <template v-else-if="deleteErrorMsg === 'used_in_norm'">
            <div class="del-info-box del-info-box--error mb-4">
              <v-icon size="20" color="#b91c1c" class="mr-3 flex-shrink-0">mdi-file-tree-outline</v-icon>
              <span>Artikl je sastojak u normativu. Uklonite ga iz normativa prije brisanja.</span>
            </div>
            <div class="del-table-label">KORISTI SE U NORMATIVU</div>
            <div class="del-table">
              <div v-for="n in normUsedIn" :key="n.id" class="del-table-row">
                <v-icon size="16" color="#94a3b8" class="mr-2">mdi-cube-outline</v-icon>
                <span>{{ n.name }}</span>
              </div>
            </div>
          </template>

          <!-- Tehnička greška -->
          <div v-else-if="deleteErrorMsg === 'error'" class="del-info-box del-info-box--error">
            <v-icon size="20" color="#b91c1c" class="mr-3">mdi-alert-circle-outline</v-icon>
            <span>Došlo je do greške. Pokušajte ponovo.</span>
          </div>

          <!-- Potvrda -->
          <template v-else>
            <div class="del-info-box del-info-box--confirm">
              <v-icon size="20" color="#b91c1c" class="mr-3 flex-shrink-0">mdi-alert-remove-outline</v-icon>
              <span>Ova akcija je <strong>nepovratna</strong>. Artikl će biti trajno uklonjen sa skladišta.</span>
            </div>
          </template>

        </v-card-text>

        <!-- Actions -->
        <v-divider></v-divider>
        <v-card-actions class="pb-5 px-6" style="flex-shrink: 0;">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="del-btn-cancel"
            @click="deleteDialog = false"
          >
            Zatvori
          </v-btn>
          <v-btn
            v-if="!deleteErrorMsg"
            elevation="0"
            class="del-btn-delete"
            :loading="deleteLoading"
            :disabled="deleteLoading"
            @click="confirmDelete"
          >
            <v-icon small class="mr-2">mdi-delete-outline</v-icon>
            Obriši artikl
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import flow from '@/mixins/flow'
import applicationSettings from '@/mixins/applicationSettings' // don't move.. detaches firebase listeners
import barcodeChecker from '@/mixins/barcodeChecker'
import addWarehouseModuleItem from '@/modules/point-of-sale/components/warehouse/warehouse-module/AddWarehouseModuleItem'
// import editWarehouseModuleItem from '@/modules/point-of-sale/components/warehouse/warehouse-module/EditWarehouseModuleItem'
import EventBus from '@/plugins/event-bus'
import { collection, query, orderBy, getDocs, where, limit, startAfter, onSnapshot, doc, deleteDoc, getDoc } from 'firebase/firestore'
import triGram from '@/mixins/trigram'

export default {
  name: 'WarehouseItems',
  inject: ['showLoader', 'hideLoader'],
  mixins: [flow, applicationSettings, barcodeChecker, triGram],
  components: { addWarehouseModuleItem },
  data: () => ({
    dialog: false,
    loading: false,
    isLoading: false,
    totalArticles: 0,
    lastiItem: false,
    items: [],
    allWarehouses: [],
    warehouses: [],
    selectedWarehouse: '',
    listeners: [],
    itemListeners: [],
    multiplier: 0,
    selectedLocation: undefined,
    locations: [],
    checkRadio: '',
    showLocationSelector: false,
    choosenText: '',
    innerSearch: undefined,
    barcode: [],
    searchEmitDestination: 'warehouseItemsSearch',
    lastDocSnapshot: null,
    searchObject: undefined,
    searchTerm: '',
    searchTimeout: null,
    deleteDialog: false,
    deleteLoading: false,
    itemToDelete: null,
    deleteErrorMsg: '',
    normUsedIn: [],
    pricelistLocations: []
  }),
  computed: {
    radioValues () {
      return [
        { name: 'Na firmi', value: 'company' },
        { name: 'Na prodajnom mjestu', value: 'pointOfSale' },
        { name: 'Sva', value: 'all' }
      ]
    },
    listView () {
      return state.isListView()
    },
    search () {
      return state.search
    },
    hasMoreItems () {
      return this.lastDocSnapshot !== null
    }
  },
  watch: {
    listView (nv, ov) {
      if (nv !== ov) {
        // View changed, reload items
        this.applyFilters()
      }
    }
  },
  async mounted () {
    this.checkRadio = 'pointOfSale'
    if (state.getPointOfSale().id === undefined || state.getPointOfSale.id === '') {
      this.checkRadio = 'company'
    }
    // FILTER STAVLJEN PRIJE JER NE STIGNE DOHVATITI PA ISPADNE DA NEMA
    // this.filterWarehouses()

    // DOHVATI SVE LOKACIJE ZA FIRMU
    await this.getLocations()

    // DOHVATI SVA SKLADISTA I PODATKE O NJIMA
    await this.getAllWarehouses()
    document.onkeydown = this.handleKeyEvents

    EventBus.$on(this.searchEmitDestination, (searchObject) => {
      this.searchObject = searchObject

      if (!searchObject.triGramObject) {
        this.lastDocSnapshot = null
        this.detachItemListeners()
        this.resetHandler()
      } else {
        this.lastDocSnapshot = null
        this.detachItemListeners()
        this.resetHandler()
        // this.getItemsNew(searchObject.triGramObject)
      }
    })

    this.getMultiplier()
  },
  methods: {
    exportItemsCsv () {
      try {
        let csvContent = 'Naziv;Šifra;Stanje\n'

        this.items.forEach((extendedItem) => {
          const item = extendedItem.item
          const name = item.name || '-'
          const code = item.code || '-'
          const stock = item.type === 'GOODS' && item.stock !== undefined
            ? item.stock.toString().replace('.', ',')
            : '-'

          const escapedName = name.includes(';') || name.includes('"')
            ? `"${name.replace(/"/g, '""')}"`
            : name

          csvContent += `${escapedName};${code};${stock}\n`
        })

        const BOM = String.fromCharCode(0xFEFF)
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', 'Artikli.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('CSV export error:', error)
        this.showMsgBox({
          text: 'Greška pri generiranju CSV datoteke.',
          actions: ['cancel'],
          cancelBtnText: 'U redu',
          color: 'error'
        })
      }
    },
    resetHandler () {
      this.items = []
      this.lastDocSnapshot = null
    },
    async getItemsNew () {
      if (this.isLoading) return 0
      this.isLoading = true

      try {
        var triGramObject = this.searchObject?.triGramObject
        if (!this.selectedWarehouse || this.selectedWarehouse === null || this.selectedWarehouse.value === '') {
          this.isLoading = false
          return 0
        }

        const searchConstraints = []
        if (triGramObject) {
          Object.keys(triGramObject).forEach(name =>
            searchConstraints.push(where(`item.meta.${name}`, '==', true))
          )
        } else {
          searchConstraints.push(orderBy('item.name', 'asc'))
        }

        if (this.lastDocSnapshot) {
          searchConstraints.push(startAfter(this.lastDocSnapshot))
        }

        searchConstraints.push(where('item.type', '==', 'GOODS'))
        searchConstraints.push(limit(10))

        const constraints = [
          collection(df, `warehouses/${this.selectedWarehouse.value}/extended_items`),
          ...searchConstraints
        ]

        const q = query.apply(this, constraints)
        const querySnapshot = await getDocs(q)

        const results = []
        querySnapshot.forEach(docSnapshot => {
          var whIt = docSnapshot.data()

          // Skip items with normativ
          if (whIt.item && whIt.item.norm !== undefined && whIt.item.norm !== null) {
            return
          }

          // Divide stock by multiplier for display
          if (whIt.item && whIt.item.stock !== undefined && this.multiplier) {
            whIt.item.stock = whIt.item.stock / this.multiplier
          }

          results.push({ _docId: docSnapshot.id, _id: whIt.id, ...whIt })

          // Attach listener to each new item
          if (whIt.item && whIt.item.id) {
            this.attachItemListener(whIt.item.id)
          }
        })

        // Add new items to existing list
        this.items.push(...results)

        if (querySnapshot.docs.length > 0) {
          this.lastDocSnapshot = querySnapshot.docs[querySnapshot.docs.length - 1]
        } else {
          this.lastDocSnapshot = null
        }

        this.isLoading = false
        return querySnapshot.docs.length
      } catch (error) {
        console.error('Error loading items:', error)
        this.isLoading = false
        return 0
      }
    },

    onSearchInput () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.applySearch()
      }, 500)
    },

    clearSearch () {
      this.searchTerm = ''
      this.searchObject = undefined
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.items = []
      if (this.selectedWarehouse && this.selectedWarehouse.value) {
        this.getItemsNew()
      }
    },

    applySearch () {
      if (this.searchTerm && this.searchTerm.trim().length > 0) {
        const triGramObject = this.triGram(this.searchTerm.trim())
        this.searchObject = { triGramObject }
      } else {
        this.searchObject = undefined
      }
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.items = []
      if (this.selectedWarehouse && this.selectedWarehouse.value) {
        this.getItemsNew()
      }
    },

    applyFilters () {
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.items = []
      if (this.selectedWarehouse && this.selectedWarehouse.value) {
        this.getItemsNew()
      }
    },

    async loadMore () {
      if (!this.hasMoreItems || this.isLoading) return

      const currentItemCount = this.items.length
      await this.getItemsNew()

      // Scroll to the last item before new items (so it appears just below header)
      this.$nextTick(() => {
        const itemCards = document.querySelectorAll('.warehouse-item-card')
        if (itemCards[currentItemCount - 1]) {
          // Scroll with offset to account for sticky header (64px header + some padding)
          const element = itemCards[currentItemCount - 1]
          const yOffset = -70 // Negative offset to position below header
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      })
    },

    attachItemListener (itemId) {
      // Check if listener already exists
      if (this.itemListeners.some(l => l.itemId === itemId)) {
        return
      }

      const itemDocRef = doc(df, `companies/${state.getCurrentCompany().id}/items/${itemId}`)

      const unsubscribe = onSnapshot(itemDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const updatedItemData = { _id: docSnapshot.id, id: docSnapshot.id, ...docSnapshot.data() }

          // Find and update the item in items array
          const index = this.items.findIndex(extItem => extItem.item && extItem.item.id === itemId)
          if (index !== -1) {
            // Preserve stock field from warehouse_items collection
            const existingStock = this.items[index].item.stock

            // Update only the item part, keep warehouse_data and stock intact
            const updatedExtendedItem = {
              ...this.items[index],
              item: {
                ...updatedItemData,
                stock: existingStock // Preserve stock from warehouse_items
              }
            }
            this.$set(this.items, index, updatedExtendedItem)
          }
        }
      }, (error) => {
        console.error(`Error listening to item ${itemId}:`, error)
      })

      this.itemListeners.push({ itemId, unsubscribe })
    },

    barcodeFind (e) {
      if (e.key !== 'Enter') { this.barcode.push(e.key) }
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        if (this.validateBarcode(this.barcode.join(''))) {
          this.innerSearch = this.barcode.join('')
          // this.innerSearchItems()
          // this.search = this.barcode.join('')
          // this.searchItems()
        } this.barcode = []
      }, 100)
    },
    validateBarcode (b) {
      var Barcoder = require('barcoder')
      return Barcoder.validate(b)
    },
    handleKeyEvents (e) {
      e = e || window.event
      this.barcodeFind(e)
    },
    filterWarehouses () {
      this.warehouses = this.allWarehouses
      switch (this.checkRadio) {
        case 'company':
          this.warehouses = this.warehouses.filter(wh => {
            if (wh.warehouse.location_id === '') {
              return wh
            }
          })
          break
        case 'pointOfSale':
          this.warehouses = this.warehouses.filter((wh, k) => {
            if (wh.warehouse.location_id === this.selectedLocation.id) {
              if (wh.warehouse.location_id === this.selectedLocation.id && wh.warehouse.selling_warehouse === true) {
                this.selectedWarehouse = this.warehouses[k]
                this.resetHandler()
              }
              return wh
            }
          })
          break
        case 'all':
      // ALREADY DONE
      }
    },
    setWarehouseChoices () {
      this.warehouses = this.allWarehouses
      this.warehouses = this.warehouses.filter(wh => {
        if (wh.warehouse.location_id === this.selectedLocation.id) {
          if (wh.warehouse.location_id === this.selectedLocation.id && wh.warehouse.selling_warehouse === true) {
            this.selectedWarehouse = wh.warehouse
            this.resetHandler()
          }
          return wh
        }
      })
    },
    getLocations () {
      this.showLocationSelector = false

      df.collection('locations')
        .where('company_id', '==', state.getCurrentCompany().id)
        .get()
        .then((documentSnapshots) => {
          if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
            documentSnapshots.docs.forEach((location) => {
              const loc = location.data()
              this.locations.push(loc)
              if (this.checkRadio === 'pointOfSale' && state.getPointOfSale().id !== undefined && loc.id === state.getPointOfSale().id) {
                this.selectedLocation = loc
                this.filterWarehouses()
              }
            })
          }
        })
    },
    async getAllWarehouses () {
      const query = df
        .collection('warehouses/')
        .where('company_id', '==', state.getCurrentCompany().id)
        .where('status', '==', 'OK')
      query.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const warehouse = doc.data()
          this.warehouses.push({ name: warehouse.name, value: warehouse.id, warehouse: warehouse })
        })
      })
      this.allWarehouses = this.warehouses
    },
    hoverOver (ref, item) {
      const container = this.$refs[ref]
      if (!container || !container[0]) return
      if (container[0].clientHeight < container[0].scrollHeight) {
        item[ref] = true
      }
    },
    openAddWarehouseModuleItemForm () {
      this.$refs.addWarehouseModuleItem.open(this.multiplier)
    },
    openDialog () {
      this.dialog = true
    },
    openEditForm (item) {
      this.$refs.editWarehouseModuleItem.open(item, this.multiplier)
    },
    refreshWarehouse (item) {
      if (item.action === 'ADD') {
        this.items.unshift({ ...item, ...{ status: 'PROCESSING' } })
        this.$vuetify.goTo(0)
      } else if (item.action === 'EDIT') {
        this.items = this.items.map((_item) => {
          if (_item.id === item.id) {
            _item.name = item.name
            _item.status = 'PROCESSING'
          }
          return _item
        })
      }
    },
    // modifyItems (change, unit) {
    //   // Filter za micanje artikala sa skladista koji imaju samo normative koji se skidaju pri izdavanju racuna
    //   // to znaci da njih uopce ne treba ispisivati koliko ih ima na stanju
    //   if (change.type === 'added') {
    //     if (
    //       this.items.some(
    //         (extendedItem) =>
    //           extendedItem.item.id === change.doc.data().item.id ||
    //           (extendedItem.item.name.toLowerCase() === change.doc.data().item.name &&
    //             extendedItem.item.status === 'PROCESSING')
    //       )
    //     ) {
    //       this.items = this.items.map((extendedItem) => {
    //         let ret = extendedItem
    //         if (
    //           extendedItem.item.id === change.doc.data().item.id ||
    //           (extendedItem.item.name === change.doc.data().item.name.toLowerCase() &&
    //             extendedItem.item.status === 'PROCESSING')
    //         ) {
    //           ret = change.doc.data()
    //           ret.item.stock = ret.item.stock / this.multiplier
    //           ret.warehouse_data.quantity_available = ret.warehouse_data.quantity_available / this.multiplier
    //         }
    //         ret.unit = unit
    //         return { ...ret, ...{ [`hover-${change.doc.data().item.id}`]: false } }
    //       })
    //     } else {
    //       const extendedItem = change.doc.data()
    //       extendedItem.item.stock = extendedItem.item.stock / this.multiplier
    //       extendedItem.warehouse_data.quantity_available = extendedItem.warehouse_data.quantity_available / this.multiplier
    //       extendedItem.unit = unit
    //       this.items.push({
    //         ...extendedItem,
    //         ...{ [`hover-${extendedItem.item.id}`]: false }
    //       })
    //     }
    //   }
    //   if (change.type === 'modified') {
    //     this.items = this.items.map((extendedItem) => {
    //       let ret = extendedItem
    //       if (extendedItem.item.id === change.doc.data().item.id) {
    //         ret = change.doc.data()
    //         ret.item.stock = ret.item.stock / this.multiplier
    //         ret.warehouse_data.quantity_available = ret.warehouse_data.quantity_available / this.multiplier
    //         ret.unit = unit
    //       }
    //       return { ...ret, ...{ [`hover-${change.doc.data().item.id}`]: false } }
    //     })
    //   }
    // },
    // getItems () {
    //   const that = this
    //   if (this.selectedWarehouse === undefined || this.selectedWarehouse.value === '') {
    //     return
    //   }

    //   let search = 'item.name'
    //   if (this.isBarcodeSearch(this.search)) {
    //     search = 'item.barcodes'
    //   }

    //   const where = that.search ? that.search.toLowerCase().trim() : ''
    //   let query = df.collection(`warehouses/${this.selectedWarehouse.value}/extended_items`)
    //     .orderBy(search, 'asc')

    //   if (where === '') {
    //     query = query
    //       .limit(12)
    //   }

    //   if (where && search === 'item.name') {
    //     query = query
    //       .where(search.trim(), '>=', where)
    //       .where(search.trim(), '<=', where + '\uf8ff')
    //   }

    //   if (where && search === 'item.barcodes') {
    //     query = query
    //       .where('item.barcodes', 'array-contains', state.search)
    //   }

    //   query.onSnapshot(
    //     (doc) => {
    //       doc.docChanges().forEach((change) => {
    //         if (change && change.doc.data() && change.doc.data().id) {
    //           df.doc(`companies/${state.getCurrentCompany()?.id}/items/${change.doc.data().id}`)
    //             .get()
    //             .then((it) => {
    //               if (it && it.data()) {
    //                 var normOnSale = false

    //                 let unit = change.doc.data().selling_unit
    //                 if (it && it.data() && it.data().warehouse_units !== undefined && it.data().warehouse_units.units !== undefined && it.data().warehouse_units.base_unit !== '') {
    //                   unit = it.data().warehouse_units.units[it.data().warehouse_units.base_unit].storage_unit
    //                 }

    //                 if (it.data() !== undefined && it.data().norm !== undefined && it.data().norm.components !== undefined) {
    //                   const item = it.data()
    //                   if (item.norm.type === 'ON_SALE') {
    //                     normOnSale = true
    //                   }
    //                 }

    //                 if (!normOnSale) {
    //                   that.modifyItems(change, unit)
    //                 }
    //               }
    //             })
    //         }
    //       })
    //     }
    //   )
    // },
    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        this.config = doc.data()
        this.multiplier = doc.data().item_amount.decimal_multiplier
      })
      this.listeners.push(listener)
    },

    detachItemListeners () {
      this.itemListeners.forEach(listener => {
        if (listener && listener.unsubscribe) {
          listener.unsubscribe()
        }
      })
      this.itemListeners = []
    },

    async openDeleteDialog (extendedItem) {
      this.itemToDelete = extendedItem
      this.deleteErrorMsg = ''
      this.normUsedIn = []
      this.pricelistLocations = []
      this.deleteLoading = true
      this.deleteDialog = true

      // 1. Provjeri je li stanje zalihe različito od 0
      const stock = extendedItem.item?.stock ?? 0
      if (stock !== 0) {
        this.deleteErrorMsg = 'nonzero_stock'
        this.deleteLoading = false
        return
      }

      try {
        const itemId = extendedItem.item?.id

        // 2. Provjeri je li artikl aktivan na cjeniku nekog prodajnog mjesta
        if (itemId && this.locations.length > 0) {
          const pricelistSnaps = await Promise.all(
            this.locations.map(loc =>
              df.doc(`location_pricelist/${loc.id}/items/${itemId}`).get()
            )
          )
          const active = this.locations.filter((loc, i) => {
            if (!pricelistSnaps[i].exists) return false
            const data = pricelistSnaps[i].data()
            return data && (data.prices || data.name || data.active !== undefined)
          })
          if (active.length > 0) {
            this.pricelistLocations = active.map(loc => loc.name || loc.id)
            this.deleteErrorMsg = 'on_pricelist'
            this.deleteLoading = false
            return
          }
        }
        if (itemId) {
          const normQuery = query(
            collection(df, `companies/${state.getCurrentCompany().id}/items`),
            where('norm', '!=', null)
          )
          const snapshot = await getDocs(normQuery)
          const usedIn = []
          snapshot.docs.forEach(d => {
            const data = d.data()
            const components = data?.norm?.components
            if (!components || typeof components !== 'object') return
            const isUsed = Object.keys(components).includes(itemId) ||
              Object.values(components).some(c => c.item_id === itemId)
            if (isUsed) {
              usedIn.push({ id: d.id, name: data.name || data.item?.name || d.id })
            }
          })
          this.normUsedIn = usedIn
          if (usedIn.length > 0) {
            this.deleteErrorMsg = 'used_in_norm'
          }
        }
      } catch (e) {
        console.error('Greška pri provjeri normativa:', e)
        this.deleteErrorMsg = 'error'
      } finally {
        this.deleteLoading = false
      }
    },

    async confirmDelete () {
      if (!this.itemToDelete || this.deleteErrorMsg) return
      this.deleteLoading = true
      try {
        await deleteDoc(
          doc(df, `warehouses/${this.selectedWarehouse.value}/extended_items/${this.itemToDelete._docId}`)
        )
        this.items = this.items.filter(i => i._docId !== this.itemToDelete._docId)
        this.deleteDialog = false
        this.itemToDelete = null
      } catch (e) {
        console.error('Greška pri brisanju artikla:', e)
        this.deleteErrorMsg = 'Došlo je do greške pri brisanju.'
      } finally {
        this.deleteLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* Table Header with Filters - Sticky */
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 14px 16px 14px;
  border-left: 3px solid #00BCD4;
  border-bottom: 1px solid #EEF2F5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Search Button */
.search-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  padding: 0 20px !important;
  height: 36px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}

.search-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.search-btn .v-icon {
  color: #3989C9 !important;
  font-size: 18px !important;
}

/* Load More Button */
.page-nav-btn-header {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.2s ease !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
  height: 36px !important;
  min-width: 110px !important;
}

.page-nav-btn-header:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.page-nav-btn-header:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(245, 245, 245, 0.8) !important;
  border-color: rgba(200, 200, 200, 0.3) !important;
  color: rgba(158, 158, 158, 0.6) !important;
}

/* Loading Overlay */
.loading-overlay-receipts {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1; /* Below header (2) but above normal content */
  border-radius: 16px !important;
  min-height: 400px;
}

.loading-spinner {
  color: #3989C9 !important;
}

/* Row body wrapper */
.warehouse-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}

/* Warehouse Item Cards */
.warehouse-item-card {
  border: 1px solid #EEF2F5 !important;
  border-left: 3px solid #E0E0E0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  overflow: hidden !important;
  margin-bottom: 4px !important;
  transition: border-left-color 0.15s ease, border-color 0.15s ease, background 0.15s ease !important;
}

.warehouse-item-card:hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}

.warehouse-item-card.status-ok     { border-left-color: #4ADE80 !important; }
.warehouse-item-card.status-warn   { border-left-color: #FBBF24 !important; }
.warehouse-item-card.status-failed { border-left-color: #F87171 !important; }

.wh-item-name {
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wh-cell-value {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
}

.one-liner-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Delete modal ──────────────────────────────────────────── */

.del-modal-card {
  border-radius: 16px !important;
  overflow: hidden !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14) !important;
}

.del-modal-header {
  position: relative;
  padding: 20px 24px;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.55) 0%,
    rgba(57, 137, 201, 0.5) 50%,
    rgba(97, 84, 164, 0.55) 100%);
  overflow: hidden;
}

.del-modal-header-gloss {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
  pointer-events: none;
}

.del-modal-header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.del-modal-title {
  color: white !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 4px rgba(0,0,0,0.15);
  line-height: 1.3;
}

.del-modal-subtitle {
  color: rgba(255,255,255,0.8) !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  margin-top: 2px;
  letter-spacing: 0.2px;
}

.del-modal-close-btn {
  background: rgba(255,255,255,0.15) !important;
}

.del-modal-close-btn:hover {
  background: rgba(255,255,255,0.25) !important;
}

/* Info boxes */
.del-info-box {
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.del-info-box--warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
}

.del-info-box--error {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #7f1d1d;
}

.del-info-box--confirm {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #7f1d1d;
}

/* Detail row */
.del-detail-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Table */
.del-table-label {
  font-size: 11px;
  font-weight: 700;
  color: #3989C9;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
  padding-left: 2px;
}

.del-table {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.del-table-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
}

.del-table-row:last-child {
  border-bottom: none;
}

.del-table-row:nth-child(even) {
  background: #f8fafc;
}

/* Buttons */
.del-btn-cancel {
  color: #3989C9 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 18px !important;
}

.del-btn-delete {
  background: linear-gradient(135deg, #f43f5e 0%, #dc2626 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 22px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3) !important;
}

.del-btn-delete:hover {
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4) !important;
}
</style>
